import { AccountActions, UserActions } from '@portal/actions';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import { getRouteStackPath } from '@portal/config/routes';
import { EventType } from '@portal/enum/analytics';
import useReduxState from '@portal/hooks/useReduxState';
import {
  AccountConfigurationFieldsTypes,
  AccountSettingKeyEnum,
  AccountTaxIdentifierType,
  AppSettingsKeyEnum,
} from '@portal/models/enumerators';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import { getEnumDescription, isEnumValueMapped } from '@portal/utils/enum';
import { keepOnlyNumbers } from '@portal/utils/format';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as MessageService from '~/services/message';
import './Configuration.less';

import {
  MinusOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { SET_THEME } from '@portal/actions/actionTypes';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { THEME_CONFIG, THEME_NAMES, ThemeType } from '@portal/enum/ThemeType';
import { useNavigation } from '@portal/hooks/useNavigation';

interface ConfigurationItem {
  id: string;
  key: AppSettingsKeyEnum | AccountSettingKeyEnum;
  value: string;
  description?: string | null;
  type?: AccountConfigurationFieldsTypes | null;
}

interface StepperProps {
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}

const Configuration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    user: { me, myUsersCount },
    account: { config },
    paymentAccount: { selectedPaymentAccount },
    theme: { currentTheme },
  } = useReduxState();

  const [formConfig, setFormConfig] = useState<ConfigurationItem[]>([]);
  const [prevFormConfig, setPrevFormConfig] = useState<ConfigurationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [warnings, setWarnings] = useState<Record<string, string>>({});
  const [selectedTheme, setSelectedTheme] = useState<ThemeType>(currentTheme);
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>(
    {}
  );

  const isPF = me.account.type === AccountTaxIdentifierType.PERSON;

  const filteredConfig = isPF
    ? config.filter((row) => row.key !== AppSettingsKeyEnum.REQUIRED_APPROVALS)
    : config;

  useEffect(() => {
    const theme = localStorage.getItem('selected-theme');
    setSelectedTheme(
      theme && Object.values(ThemeType).includes(theme as ThemeType)
        ? (theme as ThemeType)
        : ThemeType.DEFAULT
    );
  }, [currentTheme]);

  const validateApprovalSettings = (
    key: AppSettingsKeyEnum | AccountSettingKeyEnum,
    value: string
  ): string | null => {
    const numValue = parseInt(value, 10);
    const totalUsers = myUsersCount || 0;

    if (
      key === AccountSettingKeyEnum.CNAB_REQUIRED_APPROVALS ||
      key === AccountSettingKeyEnum.ACCOUNT_MIN_REQUEST_APPROVALS
    ) {
      if (numValue > totalUsers) {
        return 'PAGES.MY_ACCOUNT.CONFIGURATION.VALIDATION.APPROVALS_EXCEED_USERS';
      }
    }

    return null;
  };

  const getApprovalWarning = (
    key: AppSettingsKeyEnum | AccountSettingKeyEnum,
    value: string
  ): string | null => {
    const numValue = parseInt(value, 10);

    if (
      key === AccountSettingKeyEnum.CNAB_REQUIRED_APPROVALS ||
      key === AccountSettingKeyEnum.ACCOUNT_MIN_REQUEST_APPROVALS
    ) {
      if (numValue === 1) {
        return 'PAGES.MY_ACCOUNT.CONFIGURATION.VALIDATION.SINGLE_APPROVER_RISK';
      }
    }

    return null;
  };

  const onFormConfigChange = (
    key: AppSettingsKeyEnum | AccountSettingKeyEnum,
    value: string
  ) => {
    const fieldIndex = formConfig.findIndex((row) => row.key === key);
    if (fieldIndex === -1) return;

    const newConfig = [...formConfig];
    newConfig[fieldIndex] = {
      ...formConfig[fieldIndex],
      value,
    };
    setFormConfig(newConfig);

    setPendingChanges((prev) => ({
      ...prev,
      [key]: prevFormConfig[fieldIndex]?.value !== value,
    }));

    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }

    const warning = getApprovalWarning(key, value);
    if (warning) {
      setWarnings((prev) => ({
        ...prev,
        [key]: warning,
      }));
    } else {
      setWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[key];
        return newWarnings;
      });
    }

    sendEvent(EventType.CHANGE_CNAB_VOTE_LIMIT);
  };

  const onSubmitFormConfig = async (
    key: AppSettingsKeyEnum | AccountSettingKeyEnum
  ) => {
    const fieldIndex = formConfig.findIndex((row) => row.key === key);

    if (fieldIndex === -1) {
      return;
    }
    const newConfig = formConfig[fieldIndex];

    if (prevFormConfig[fieldIndex]?.value === formConfig[fieldIndex].value) {
      return;
    }

    if (newConfig.value.length === 0) {
      setFormConfig(prevFormConfig);
      return MessageService.error('NOT_EMPTY');
    }

    const validationError = validateApprovalSettings(key, newConfig.value);
    if (validationError) {
      setValidationErrors((prev) => ({
        ...prev,
        [key]: validationError,
      }));
      return;
    }

    if (me.id) {
      setLoading(true);
      try {
        const configToSave = {
          ...newConfig,
          id: newConfig.id || `new-${Date.now()}`,
        } as models.Config;

        await dispatch(
          AccountActions.putUserConfig(configToSave.id, configToSave)
        );
        MessageService.success(
          'PAGES.MY_ACCOUNT.CONFIGURATION.MESSAGE.SUCCESS_UPDATE'
        );
        setPrevFormConfig((prev) => {
          const updated = [...prev];
          updated[fieldIndex] = { ...formConfig[fieldIndex] };
          return updated;
        });
        setPendingChanges((prev) => {
          const newChanges = { ...prev };
          delete newChanges[key];
          return newChanges;
        });

        if (validationErrors[key]) {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[key];
            return newErrors;
          });
        }
        if (warnings[key]) {
          setWarnings((prev) => {
            const newWarnings = { ...prev };
            delete newWarnings[key];
            return newWarnings;
          });
        }
      } catch (error) {
        MessageService.error(
          'PAGES.MY_ACCOUNT.CONFIGURATION.MESSAGE.ERROR_UPDATE'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const onCancelFormConfig = (
    key: AppSettingsKeyEnum | AccountSettingKeyEnum
  ) => {
    const fieldIndex = formConfig.findIndex((row) => row.key === key);
    if (fieldIndex === -1) return;

    const newConfig = [...formConfig];
    newConfig[fieldIndex] = {
      ...formConfig[fieldIndex],
      value: prevFormConfig[fieldIndex]?.value || '',
    };
    setFormConfig(newConfig);
    setPendingChanges((prev) => {
      const newChanges = { ...prev };
      delete newChanges[key];
      return newChanges;
    });

    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
    }

    if (warnings[key]) {
      setWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[key];
        return newWarnings;
      });
    }
  };

  useEffect(() => {
    if (me.id) {
      dispatch(
        AccountActions.getUserConfig(
          selectedPaymentAccount !== null
            ? selectedPaymentAccount.id
            : me.account.paymentAccounts.find(
                (account) => account.type === PaymentAccountType.ACCOUNT
              )?.id ?? ''
        )
      );

      dispatch(UserActions.myUsersCount());
    }
  }, [dispatch, me.id, selectedPaymentAccount?.id]);

  useEffect(() => {
    if (filteredConfig.length > 0) {
      setFormConfig(filteredConfig);
      setPrevFormConfig(filteredConfig);
    }
  }, [filteredConfig]);

  useEffect(() => {
    if (formConfig.length > 0 && myUsersCount !== null) {
      const newValidationErrors: Record<string, string> = {};
      const newWarnings: Record<string, string> = {};

      formConfig.forEach((field) => {
        const validationError = validateApprovalSettings(
          field.key,
          field.value
        );
        if (validationError) {
          newValidationErrors[field.key] = validationError;
        }

        const warning = getApprovalWarning(field.key, field.value);
        if (warning) {
          newWarnings[field.key] = warning;
        }
      });

      setValidationErrors(newValidationErrors);
      setWarnings(newWarnings);
    }
  }, [formConfig, myUsersCount]);

  const handleThemeChange = async (theme: ThemeType) => {
    localStorage.setItem('selected-theme', theme);
    dispatch({ type: SET_THEME, payload: theme });
  };

  const Stepper = ({ value, min, max, disabled, onChange }: StepperProps) => (
    <div className="approval-stepper">
      <button
        disabled={disabled || value <= min}
        onClick={() => onChange(value - 1)}
      >
        <MinusOutlined />
      </button>
      <span className="approval-stepper__value">{value}</span>
      <button
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
      >
        <PlusOutlined />
      </button>
    </div>
  );

  const renderThemeCard = () => {
    return (
      <div className="configuration-card">
        <div>
          <label className="configuration-card__label">
            {translate('PAGES.MY_ACCOUNT.TABS.THEME_SELECTION')}
          </label>
          <div className="configuration-card__help">
            {translate('PAGES.MY_ACCOUNT.TABS.THEME_DESCRIPTION')}
          </div>
        </div>
        <div className="configuration-card__theme-container">
          {Object.values(ThemeType).map((theme) => (
            <div
              key={theme}
              className={`theme-option ${
                selectedTheme === theme ? 'theme-option--selected' : ''
              }`}
              onClick={() => handleThemeChange(theme)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleThemeChange(theme);
                }
              }}
            >
              <div
                className="theme-option__preview"
                style={{
                  background: THEME_CONFIG[theme].background,
                }}
              />
              <div className="theme-option__label">{THEME_NAMES[theme]}</div>
              {selectedTheme === theme && (
                <div className="theme-option__check">✓</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderConfigurationField = (row: ConfigurationItem) => {
    const fieldValue = formConfig.find((field) => field.key === row.key)?.value;
    const isNumberField = row.type === AccountConfigurationFieldsTypes.NUMBER;
    const validationError = validationErrors[row.key];
    const warning = warnings[row.key];
    const isApprovalField =
      row.key === AccountSettingKeyEnum.CNAB_REQUIRED_APPROVALS ||
      row.key === AccountSettingKeyEnum.ACCOUNT_MIN_REQUEST_APPROVALS;
    const hasPendingChanges = pendingChanges[row.key];

    if (isApprovalField) {
      const numValue = parseInt(fieldValue || '0', 10);
      const totalUsers = myUsersCount || 0;

      return (
        <div className="configuration-card" key={row.key}>
          <div>
            <label className="configuration-card__label">
              {getEnumDescription(row.key, 'ACCOUNT_CONFIGURATION_FIELDS')}
            </label>
          </div>
          <div className="configuration-card__input-container">
            <Stepper
              value={numValue}
              min={1}
              max={totalUsers}
              disabled={loading}
              onChange={(value) => {
                onFormConfigChange(row.key, value.toString());
              }}
            />
            {hasPendingChanges ? (
              <div className="configuration-card__actions">
                <AdvancedButton
                  text={translate(
                    'PAGES.MY_ACCOUNT.CONFIGURATION.BUTTONS.CANCEL'
                  )}
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={loading}
                  onClick={() => onCancelFormConfig(row.key)}
                />
                <AdvancedButton
                  text={translate(
                    'PAGES.MY_ACCOUNT.CONFIGURATION.BUTTONS.SAVE'
                  )}
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={loading || !!validationError}
                  loading={loading}
                  onClick={() => onSubmitFormConfig(row.key)}
                />
              </div>
            ) : null}
            {validationError ? (
              <div className="configuration-card__error">
                <AdvancedAlert severity="error">
                  {translate(validationError)}
                </AdvancedAlert>
              </div>
            ) : null}
            {warning ? (
              <div className="configuration-card__warning">
                <AdvancedAlert severity="warning">
                  {translate(warning)}
                </AdvancedAlert>
              </div>
            ) : null}
          </div>
        </div>
      );
    }

    return (
      <div className="configuration-card" key={row.key}>
        <div>
          <label className="configuration-card__label">
            {getEnumDescription(row.key, 'ACCOUNT_CONFIGURATION_FIELDS')}
          </label>
          {isNumberField ? (
            <div className="configuration-card__help">
              {translate(
                'PAGES.MY_ACCOUNT.CONFIGURATION.FIELD_HELP.NUMBER_ONLY'
              )}
            </div>
          ) : null}
        </div>
        <div className="configuration-card__input-container">
          <AdvancedInput
            type="text"
            style={{
              marginTop: '8px',
            }}
            variant="outlined"
            value={fieldValue || ''}
            onChange={(value) => {
              if (isNumberField) {
                return onFormConfigChange(row.key, keepOnlyNumbers(value));
              }
              onFormConfigChange(row.key, value);
            }}
            onBlur={() => onSubmitFormConfig(row.key)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                (
                  event.target as typeof event.target & {
                    blur: () => void;
                  }
                ).blur();
              }
            }}
            disabled={loading}
            className={`configuration-card__input ${
              validationError ? 'configuration-card__input--error' : ''
            }`}
          />
          {validationError ? (
            <div className="configuration-card__error">
              <AdvancedAlert severity="error">
                {translate(validationError)}
              </AdvancedAlert>
            </div>
          ) : null}
          {warning ? (
            <div className="configuration-card__warning">
              <AdvancedAlert severity="warning">
                {translate(warning)}
              </AdvancedAlert>
            </div>
          ) : null}
          {loading ? <div className="configuration-card__loading" /> : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.MY_ACCOUNT.CONFIGURATION.TITLE'),
          },
        ]}
      />
      <div className="configuration-page">
        <AdvancedPanelContentHeader
          testId="configuration-panel-header"
          title={translate('PAGES.MY_ACCOUNT.CONFIGURATION.TITLE')}
          description={translate('PAGES.MY_ACCOUNT.CONFIGURATION.DESCRIPTION')}
          goBackAction={() => navigate.back()}
        />
        <div className="configuration-container">
          <div className="configuration-grid">
            {renderThemeCard()}
            {filteredConfig?.length > 0 ? (
              filteredConfig
                ?.filter(
                  (row) =>
                    row.key !==
                      AppSettingsKeyEnum.ACCOUNT_SETTINGS_DECISION_DESK_RETRY_TRANSACTION_MIN &&
                    isEnumValueMapped(row.key, 'ACCOUNT_CONFIGURATION_FIELDS')
                )
                ?.map((row) => renderConfigurationField(row))
            ) : (
              <div className="configuration-empty">
                <div className="configuration-empty__icon">
                  <PaperClipOutlined />
                </div>
                <ThemedText variant="h3" className="configuration-empty__title">
                  {translate('PAGES.MY_ACCOUNT.CONFIGURATION.NO_CONFIG_TITLE')}
                </ThemedText>
                <ThemedText
                  variant="body"
                  className="configuration-empty__description"
                >
                  {translate('PAGES.MY_ACCOUNT.CONFIGURATION.NO_CONFIG')}
                </ThemedText>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Configuration;
